import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { Language } from '../types';
import { transcribeAndRefineAudio } from '../services/geminiService';

interface Props {
    language: Language;
    pageCanvases: React.MutableRefObject<Map<number, HTMLCanvasElement>>;
    parentNoteName?: string;
    onComplete: (imageBase64: string, transcript: string) => void;
    onCancel: () => void;
}

const MIN_SIZE = 80;

export const MobileVoiceCaptureOverlay: React.FC<Props> = ({
    language, pageCanvases, parentNoteName, onComplete, onCancel,
}) => {
    const isVi = language === 'vietnamese';

    const [frame, setFrame] = useState(() => {
        const w = Math.min(window.innerWidth * 0.82, window.innerWidth - 32);
        const h = Math.min(window.innerHeight * 0.38, 300);
        return {
            x: (window.innerWidth - w) / 2,
            y: (window.innerHeight - h) / 2 - 50,
            w,
            h,
        };
    });

    const [isRecording, setIsRecording] = useState(false);
    const [duration, setDuration] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);

    const mrRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const dragRef = useRef<{ type: string; sx: number; sy: number; sf: typeof frame } | null>(null);

    useEffect(() => {
        startRecording();
        return () => cleanup();
    }, []);

    const cleanup = () => {
        if (mrRef.current?.state === 'recording') mrRef.current.stop();
        streamRef.current?.getTracks().forEach(t => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mr = new MediaRecorder(stream);
            mrRef.current = mr;
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.start();
            setIsRecording(true);
            timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
        } catch {
            // mic access denied — still allow frame capture without audio
        }
    };

    const captureFrame = (): string | null => {
        let bestCanvas: HTMLCanvasElement | null = null;
        let bestOverlap = 0;

        for (const [, canvas] of pageCanvases.current) {
            const r = canvas.getBoundingClientRect();
            const ox = Math.max(0, Math.min(frame.x + frame.w, r.right) - Math.max(frame.x, r.left));
            const oy = Math.max(0, Math.min(frame.y + frame.h, r.bottom) - Math.max(frame.y, r.top));
            if (ox * oy > bestOverlap) {
                bestOverlap = ox * oy;
                bestCanvas = canvas;
            }
        }

        if (!bestCanvas || bestOverlap === 0) return null;

        const r = bestCanvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const cropL = Math.max(frame.x, r.left);
        const cropT = Math.max(frame.y, r.top);
        const cropR = Math.min(frame.x + frame.w, r.right);
        const cropB = Math.min(frame.y + frame.h, r.bottom);
        const cropW = cropR - cropL;
        const cropH = cropB - cropT;
        if (cropW <= 0 || cropH <= 0) return null;

        const srcX = (cropL - r.left) * dpr;
        const srcY = (cropT - r.top) * dpr;
        const srcW = cropW * dpr;
        const srcH = cropH * dpr;

        const out = document.createElement('canvas');
        out.width = Math.round(srcW);
        out.height = Math.round(srcH);
        const ctx = out.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.drawImage(
            bestCanvas,
            Math.round(srcX), Math.round(srcY), Math.round(srcW), Math.round(srcH),
            0, 0, out.width, out.height
        );
        return out.toDataURL('image/png');
    };

    const handleDone = async () => {
        setIsProcessing(true);
        if (timerRef.current) clearInterval(timerRef.current);

        // Capture image synchronously while overlay is still visible
        const imageBase64 = captureFrame();

        // Stop recording
        const mr = mrRef.current;
        if (mr && mr.state === 'recording') {
            await new Promise<void>(resolve => { mr.onstop = () => resolve(); mr.stop(); });
        }
        streamRef.current?.getTracks().forEach(t => t.stop());

        let transcript = '';
        if (chunksRef.current.length > 0) {
            try {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                const b64 = await new Promise<string>(resolve => {
                    const reader = new FileReader();
                    reader.onload = () => resolve((reader.result as string).split(',')[1]);
                    reader.readAsDataURL(blob);
                });
                transcript = await transcribeAndRefineAudio(b64, language);
            } catch {
                transcript = '';
            }
        }

        if (imageBase64) {
            onComplete(imageBase64, transcript);
        } else {
            onCancel();
        }
    };

    const startDrag = (e: React.PointerEvent, type: string) => {
        e.stopPropagation();
        e.preventDefault();
        (e.target as Element).setPointerCapture(e.pointerId);
        dragRef.current = { type, sx: e.clientX, sy: e.clientY, sf: { ...frame } };
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!dragRef.current) return;
        const { type, sx, sy, sf } = dragRef.current;
        const dx = e.clientX - sx;
        const dy = e.clientY - sy;
        const W = window.innerWidth, H = window.innerHeight;

        setFrame(() => {
            switch (type) {
                case 'move': return {
                    ...sf,
                    x: Math.max(0, Math.min(W - sf.w, sf.x + dx)),
                    y: Math.max(0, Math.min(H - sf.h, sf.y + dy)),
                };
                case 'br': return { ...sf, w: Math.max(MIN_SIZE, sf.w + dx), h: Math.max(MIN_SIZE, sf.h + dy) };
                case 'bl': {
                    const newW = Math.max(MIN_SIZE, sf.w - dx);
                    return { x: sf.x + sf.w - newW, y: sf.y, w: newW, h: Math.max(MIN_SIZE, sf.h + dy) };
                }
                case 'tr': {
                    const newH = Math.max(MIN_SIZE, sf.h - dy);
                    return { x: sf.x, y: sf.y + sf.h - newH, w: Math.max(MIN_SIZE, sf.w + dx), h: newH };
                }
                case 'tl': {
                    const newW = Math.max(MIN_SIZE, sf.w - dx);
                    const newH = Math.max(MIN_SIZE, sf.h - dy);
                    return { x: sf.x + sf.w - newW, y: sf.y + sf.h - newH, w: newW, h: newH };
                }
                default: return sf;
            }
        });
    };

    const fmt = (s: number) =>
        `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

    const handleStyle = (corner: string): React.CSSProperties => ({
        position: 'absolute',
        width: 22, height: 22,
        background: 'white',
        borderRadius: 4,
        touchAction: 'none',
        cursor: `${corner}-resize`,
        ...(corner.includes('t') ? { top: -8 } : { bottom: -8 }),
        ...(corner.includes('l') ? { left: -8 } : { right: -8 }),
    });

    return (
        <div
            className="fixed inset-0 z-[700]"
            style={{ touchAction: 'none' }}
            onPointerMove={onPointerMove}
            onPointerUp={() => { dragRef.current = null; }}
        >
            {/* Frame — transparent inside, box-shadow dims everything outside */}
            <div
                style={{
                    position: 'absolute',
                    left: frame.x, top: frame.y,
                    width: frame.w, height: frame.h,
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
                    border: '2.5px solid white',
                    borderRadius: 6,
                    cursor: 'move',
                    touchAction: 'none',
                    zIndex: 1,
                }}
                onPointerDown={(e) => startDrag(e, 'move')}
            >
                {(['tl', 'tr', 'bl', 'br'] as const).map(c => (
                    <div key={c} style={handleStyle(c)} onPointerDown={(e) => startDrag(e, c)} />
                ))}
            </div>

            {/* Recording indicator — floats above the frame */}
            <div
                style={{
                    position: 'absolute',
                    left: frame.x,
                    top: Math.max(10, frame.y - 40),
                    zIndex: 2,
                    pointerEvents: 'none',
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-black/80 backdrop-blur-sm rounded-full text-white text-xs font-medium"
            >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-gray-400'}`} />
                <Mic size={12} />
                <span>{isRecording ? fmt(duration) : (isVi ? 'Đang khởi động...' : 'Starting...')}</span>
            </div>

            {/* Hint text — top of screen */}
            <div
                style={{ position: 'absolute', top: 10, left: 0, right: 0, zIndex: 2, pointerEvents: 'none' }}
                className="flex justify-center"
            >
                <div className="px-4 py-1.5 bg-black/70 backdrop-blur-sm rounded-full text-white/90 text-xs text-center max-w-[85%]">
                    {isVi
                        ? 'Kéo khung vào nội dung cần chụp · Nói giải thích của bạn'
                        : 'Drag frame over content · Speak your explanation'}
                </div>
            </div>

            {/* Parent note label */}
            {parentNoteName && (
                <div
                    style={{ position: 'absolute', bottom: 90, left: 0, right: 0, zIndex: 2, pointerEvents: 'none' }}
                    className="flex justify-center"
                >
                    <div className="px-3 py-1 bg-black/60 rounded-full text-white/70 text-xs">
                        {isVi ? `Thêm vào: ${parentNoteName}` : `Attach to: ${parentNoteName}`}
                    </div>
                </div>
            )}

            {/* Cancel / Done buttons */}
            <div
                style={{ position: 'absolute', bottom: 32, left: 0, right: 0, zIndex: 2 }}
                className="flex items-center justify-center gap-5"
            >
                <button
                    onClick={() => { cleanup(); onCancel(); }}
                    disabled={isProcessing}
                    className="px-7 py-3.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm font-medium border border-white/20 active:scale-95 transition-transform disabled:opacity-50"
                >
                    {isVi ? 'Huỷ' : 'Cancel'}
                </button>
                <button
                    onClick={handleDone}
                    disabled={isProcessing}
                    className="px-8 py-3.5 rounded-full bg-white text-gray-900 text-sm font-bold flex items-center gap-2 shadow-lg active:scale-95 transition-transform disabled:opacity-70"
                >
                    {isProcessing ? (
                        <>
                            <Loader2 size={15} className="animate-spin" />
                            <span>{isVi ? 'Đang tạo...' : 'Creating...'}</span>
                        </>
                    ) : (
                        <>
                            <Square size={13} className="fill-gray-900" />
                            <span>{isVi ? 'Xong' : 'Done'}</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};
